import type { EntryChangeWindow } from "./queries/competitions";
import type { EntryLockReason, EntryPermissions } from "./queries/entries";

/**
 * Centralized Czech copy for the entry window (Modul 5): the club's
 * taper from „všechno jde" down to „nic nejde", and why one particular
 * přihláška is narrower than its leader expects.
 *
 * ENTRY_LOCK_MESSAGES is a CONTRACT with internal/service/entry. The
 * lock reasons are machine keys emitted by Go, and a key that arrives
 * without a sentence here renders as NOTHING: no type error, no failing
 * test, just a dialog with no explanation in it. Mirrors musicState.ts;
 * e2e/specs/prihlaska/contract.spec.ts compares both sides off disk.
 *
 * Every sentence names who can still do it. A club told only that
 * something „nejde" writes an e-mail asking why.
 */
export const ENTRY_LOCK_MESSAGES: Record<EntryLockReason, string> = {
  entry_deadline:
    "Uzávěrka přihlášek proběhla. Novou přihlášku už podat nejde; změny po uzávěrce provede pořadatel soutěže.",
  category_deadline:
    "Uzávěrka změn kategorie proběhla. Kombinaci už změnit nejde; další změny provede pořadatel soutěže.",
  roster_deadline:
    "Uzávěrka změn soutěžících proběhla. Změny přihlášky po uzávěrce provede pořadatel soutěže.",
  start_list_published:
    "Pořadatel zveřejnil startovní listinu. Přihlášky od této chvíle mění jen pořadatel soutěže.",
  withdrawn:
    "Přihláška je odhlášená. Vrátit ji zpět do pořadí může jen pořadatel soutěže.",
  competition_closed:
    "Soutěž už není ve fázi přihlašování; změny přihlášek provede pořadatel soutěže.",
  season_closed: "Soutěž patří do uzavřené sezóny; měnit lze jen aktuální sezónu.",
  competition_frozen: "Soutěž je dokončená a přihlášky už se nemění.",
  competition_running:
    "Soutěž probíhá. Změny přihlášek teď řeší pořadatel soutěže přímo na místě.",
  start_called:
    "Start už byl zavolán na plochu, přihláška se už nemění. Nedokončené vystoupení zapíše porota jako Nedokončeno (DNF).",
};

/** The alert a club's přihlášky page shows for the rung it is on. */
export interface EntryWindowNotice {
  variant: "info" | "warn";
  message: string;
}

/**
 * What the club is told about the competition as a whole, above the
 * table. Null while everything is still open: a page that says nothing
 * is how „nothing is wrong" reads.
 */
export function entryWindowNotice(window: EntryChangeWindow): EntryWindowNotice | null {
  switch (window) {
    case "open":
      return null;
    case "no_new":
      return {
        variant: "info",
        message:
          "Uzávěrka přihlášek proběhla, novou přihlášku už podat nejde. Do uzávěrky změn kategorie můžete u podaných přihlášek měnit kombinaci i soutěžící, nebo se odhlásit.",
      };
    case "roster_only":
      return {
        variant: "warn",
        message:
          "Kombinaci už u podaných přihlášek měnit nejde. Do uzávěrky změn soutěžících můžete vyměnit soutěžící, nebo se odhlásit.",
      };
    case "locked":
      return {
        variant: "warn",
        message: "Změny přihlášek jsou uzavřeny; provede je pořadatel soutěže.",
      };
  }
}

/**
 * Whether either half of an entry is still editable.
 *
 * The disjunction lives here rather than on the wire because the ladder
 * keeps it trivial: the rungs close the kategorie before the soutěžící,
 * so can_change_competitors alone would do. Written as both, because a
 * reader should not have to know that to trust it.
 */
export function canEditEntry(p: EntryPermissions): boolean {
  return p.can_change_category || p.can_change_competitors;
}

/**
 * The warning a club sees before its own odhláška, or null when there
 * is nothing to warn about.
 *
 * Read straight off the server's answer. Working it out from the
 * deadlines here would be a second copy of the rule, and this one has
 * to be right: it is the sentence between a leader and a přihláška
 * they cannot get back.
 */
export function withdrawWarning(p: EntryPermissions): string | null {
  if (!p.withdraw_is_final) return null;
  return "Uzávěrka přihlášek proběhla, takže novou přihlášku už podat nejde. Vrátit odhlášku zpět umí jen pořadatel soutěže.";
}
